
import { Link } from "react-router-dom";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, Heart, Eye } from "lucide-react";
import { Story } from "@/types";
import { useRecordStoryView } from "@/hooks/useRecordStoryView";
import { useStoryLikes } from "@/hooks/useStoryLikes";
import { cn } from "@/lib/utils";

interface StoryCardProps {
  story: Story;
}

export const StoryCard = ({ story }: StoryCardProps) => {
  // Record view when story card is rendered
  useRecordStoryView(story.id);
  
  // Handle likes
  const { likes, userHasLiked, isLoading, toggleLike } = useStoryLikes(story.id, story.likes);

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow h-full flex flex-col">
      <div className="h-48 overflow-hidden relative">
        <img
          src={story.coverImage || 'https://placehold.co/600x400?text=No+Image'}
          alt={story.title}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover"
          onError={(e) => {
            const img = e.currentTarget;
            if (img.src !== 'https://placehold.co/600x400?text=No+Image') {
              img.src = 'https://placehold.co/600x400?text=No+Image';
            }
          }}
        />
        {story.premium && (
          <Badge className="absolute top-2 right-2 bg-yellow-500">Premium</Badge>
        )}
      </div>
      <CardContent className="pt-4 flex-grow">
        <Link to={`/story/${story.id}`}>
          <h3 className="font-bold text-lg hover:text-chapterly-purple transition-colors line-clamp-1">
            {story.title}
          </h3>
        </Link>
        <Link to={`/author/${story.author.username}`} className="text-sm text-gray-500 hover:text-chapterly-purple transition-colors">
          {story.author.name}
        </Link>
        <p className="text-sm text-gray-600 mt-2 line-clamp-2">
          {story.description}
        </p>
        <div className="flex flex-wrap gap-1 mt-3">
          {story.categories.map((category) => (
            <Link key={`${story.id}-${category.id}`} to={`/category/${category.slug}`}>
              <Badge variant="secondary" className="mr-1">
                {category.name}
              </Badge>
            </Link>
          ))}
        </div>
      </CardContent>
      <CardFooter className="flex justify-between text-sm text-gray-500 border-t pt-3">
        <div className="flex items-center space-x-3">
          <div className="flex items-center">
            <Clock className="h-4 w-4 mr-1" />
            <span>{story.updateSchedule}</span>
          </div>
          {story.status && (
            <Badge variant={story.status === "ongoing" || story.status === "completed" || story.status === "hiatus" 
              ? story.status 
              : "secondary"} 
              className="ml-1">
              {story.status.charAt(0).toUpperCase() + story.status.slice(1)}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-4 pr-3">
          <div className="flex items-center">
            <Eye className="h-4 w-4 mr-1" />
            <span>{story.views.toLocaleString()}</span>
          </div>
          <button 
            onClick={(e) => {
              e.preventDefault();
              toggleLike();
            }}
            disabled={isLoading}
            className="flex items-center whitespace-nowrap group"
          >
            <Heart 
              className={cn(
                "h-4 w-4 mr-1 transition-colors", 
                userHasLiked ? "fill-red-500 text-red-500" : "group-hover:text-red-500"
              )}
            />
            <span>{likes.toLocaleString()}</span>
          </button>
        </div>
      </CardFooter>
    </Card>
  );
};
